/**
 * Widget i18n.
 *
 * Locale precedence differs from the admin app on purpose:
 *   1. `?lang=ar` in the URL - a QR code on an Arabic table tent, or a link in
 *      an Arabic WhatsApp confirmation, must open in Arabic regardless of the
 *      phone's settings.
 *   2. The browser's languages.
 *   3. The venue default (supplied by the API in Phase 6).
 *
 * No localStorage: the widget is a single-visit flow, often in an iframe on a
 * third-party origin where storage may be partitioned or blocked outright.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DIRECTION, LOCALES, type Locale } from '@yp/shared';
import en from '@yp/shared/locales/en';
import ar from '@yp/shared/locales/ar';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function resolveLocale(search: string, venueDefault: Locale = 'en'): Locale {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (isLocale(fromUrl)) return fromUrl;

  for (const candidate of navigator.languages ?? []) {
    const base = candidate.split('-')[0];
    if (isLocale(base)) return base;
  }

  return venueDefault;
}

export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = DIRECTION[locale];
}

export async function initI18n(locale: Locale): Promise<typeof i18next> {
  await i18next.use(initReactI18next).init({
    resources: { en: { translation: en }, ar: { translation: ar } },
    lng: locale,
    supportedLngs: [...LOCALES],
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  applyDocumentLocale(locale);
  return i18next;
}
